import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box, Typography, Popover, Button, TextField,
    RadioGroup, FormControlLabel, Radio, FormControl
} from '@material-ui/core';

const LABELS = [
    'Not achieved',
    'Incorrect execution',
    'Partially correct execution with teacher support',
    'Correct execution independently'
];

const buildRanges = (maxScore) => {
    const max = Number(maxScore) || 0;
    const q = Math.floor(max / 4);
    return [
        [0, q - 1],
        [q, 2 * q - 1],
        [2 * q, 3 * q - 1],
        [3 * q, max]
    ];
};

const useStyles = makeStyles((theme) => ({
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            gap: theme.spacing(1),
        },
    },
    radioLabel: {
        marginBottom: theme.spacing(0.5),
    },
}));

const CheckboxEvaluationPopover = ({ open, anchorEl, onClose, onConfirm, stepIndex, maxScore, note }) => {
    const classes = useStyles();
    const [selectedValue, setSelectedValue] = useState('');
    const [freeText, setFreeText] = useState('');
    const [isReevaluating, setIsReevaluating] = useState(false);

    const hasNote = note && note.trim() !== '';
    const showSelectionView = !hasNote || isReevaluating;

    const ranges = buildRanges(maxScore);

    const handleClose = () => {
        setSelectedValue('');
        setFreeText('');
        setIsReevaluating(false);
        onClose();
    };

    const handleConfirm = () => {
        const finalNote = freeText.trim()
            ? `${selectedValue} - ${freeText.trim()}`
            : selectedValue;
        onConfirm(finalNote);
        setSelectedValue('');
        setFreeText('');
        setIsReevaluating(false);
    };

    const handleReevaluate = () => {
        setIsReevaluating(true);
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Box style={{ padding: '16px', minWidth: '280px', maxWidth: 'min(500px, 90vw)', width: '100%' }}>
                <Typography variant="h6" component="h3" gutterBottom>
                    Evaluation Step {stepIndex + 1}
                </Typography>

                {showSelectionView ? (
                    <>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '12px' }}>
                            Select the evaluation criteria for this step
                        </Typography>

                        <FormControl component="fieldset" fullWidth>
                            <RadioGroup
                                value={selectedValue}
                                onChange={(e) => setSelectedValue(e.target.value)}
                            >
                                {LABELS.map((label, i) => {
                                    const [start, end] = ranges[i];
                                    const rangeLabel = end >= start
                                        ? `${start} - ${end} ${label}`
                                        : `${start} ${label}`;
                                    return (
                                        <FormControlLabel
                                            key={label}
                                            value={rangeLabel}
                                            control={<Radio color="primary" />}
                                            label={rangeLabel}
                                            className={classes.radioLabel}
                                        />
                                    );
                                })}
                            </RadioGroup>
                        </FormControl>

                        <TextField
                            multiline
                            minRows={4}
                            fullWidth
                            placeholder="Note"
                            variant="outlined"
                            margin="normal"
                            value={freeText}
                            onChange={(e) => setFreeText(e.target.value)}
                        />

                        <Box className={classes.buttonsContainer}>
                            <Button
                                variant="contained"
                                disabled={!selectedValue}
                                onClick={handleConfirm}
                                style={selectedValue ? { backgroundColor: '#4caf50', color: 'white' } : undefined}
                            >
                                Confirm Evaluation
                            </Button>
                        </Box>
                    </>
                ) : (
                    <>
                        <Typography variant="body1" style={{ marginBottom: '16px' }}>
                            {note}
                        </Typography>

                        <Box className={classes.buttonsContainer}>
                            <Button
                                variant="contained"
                                onClick={handleReevaluate}
                                style={{ backgroundColor: '#4caf50', color: 'white' }}
                            >
                                Reevaluate
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Popover>
    );
};

export default CheckboxEvaluationPopover;
